<template>
  <div
    class="action-node"
    :class="[runClass, { 'action-node--active': active }]"
    :data-node-id="id"
  >
    <div class="action-node__icon">
      <BaseIcon
        :name="actionTypes.find((t) => t.value === nodeData.type)?.icon || 'bullseye'"
        size="16"
      />
    </div>
    <div class="action-node__body">
      <span class="action-node__kind">Action</span>
      <h3>{{ actionTypes.find((t) => t.value === nodeData.type)?.label || nodeData.type }}</h3>
      <p class="action-node__summary">{{ compactSummary }}</p>
    </div>
    <div class="action-node__footer" v-if="!hasConnections">
      <button class="add-node-btn" type="button" @click="addNewNode">
        <BaseIcon name="plus" size="12" />
      </button>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, onMounted, inject } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  import ActionPickerModal from '../modals/ActionPickerModal.vue';
  import { useUIStore } from '@/store/uiStore';
  import { useIntegrationStore } from '@/store/integrationStore';
  import { useDealStore } from '@/store/dealStore';
  import { useUserStore } from '@/store/userStore';
  import { useVueFlow } from '@vue-flow/core';
  import { DELAY_UNITS } from '@/constants/automations';
  import OrganizationService from '@/services/organizationService';

  const organizationService = new OrganizationService();

  export default {
    name: 'ActionNode',

    components: {
      BaseIcon,
    },

    props: {
      id: {
        type: String,
        required: true,
      },
      data: {
        type: Object,
        required: true,
      },
      currentAutomation: {
        type: Object,
        required: true,
      },
      active: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['update', 'add-node'],

    setup(props, { emit }) {
      const nodeData = ref({ ...props.data });
      const uiStore = useUIStore();
      const integrationStore = useIntegrationStore();
      const dealStore = useDealStore();
      const userStore = useUserStore();
      const { getOutgoers } = useVueFlow();
      const stageCache = ref({});
      const members = ref([]);

      const hasConnections = computed(() => {
        return getOutgoers({ id: props.id }).length > 0;
      });

      const actionTypes = [
        { value: 'integration', label: 'Integration', icon: 'link' },
        { value: 'task', label: 'Task', icon: 'bullseye' },
        { value: 'condition', label: 'Condition', icon: 'split' },
        { value: 'move_deal_stage', label: 'Verplaats deal naar stage', icon: 'arrow-right' },
        { value: 'assign_user', label: 'Toewijzen aan persoon', icon: 'user' },
        { value: 'create_deal_task', label: 'Maak taak aan in deal', icon: 'clipboard' },
        { value: 'delay', label: 'Delay', icon: 'calendar-clock' },
      ];

      const TASK_TYPE_LABELS = {
        proposal: 'Create a proposal',
        celebrate: 'Celebrate',
        add_activity: 'Add activity',
      };

      const integrationName = computed(() => {
        const id = nodeData.value.config?.integrationId;
        if (!id) return '';
        const integration = integrationStore.integrations.find((i) => i.id === id);
        return integration?.name || '';
      });

      const taskTypeSummary = computed(() => {
        const taskType = nodeData.value.config?.taskType;
        if (!taskType) return '';
        if (taskType === 'celebrate' && nodeData.value.config?.celebrationMessage) {
          return nodeData.value.config.celebrationMessage;
        }
        return TASK_TYPE_LABELS[taskType] || taskType;
      });

      const delaySummary = computed(() => {
        const cfg = nodeData.value.config || {};
        const duration = parseInt(cfg.duration, 10);
        if (!Number.isInteger(duration) || duration < 1) return '';
        const unitDef = DELAY_UNITS.find((u) => u.value === cfg.unit);
        if (!unitDef) return `Wacht ${duration}`;
        const SINGULAR_NL = {
          minutes: 'minuut',
          hours: 'uur',
          days: 'dag',
          weeks: 'week',
        };
        const label =
          duration === 1
            ? SINGULAR_NL[cfg.unit] || unitDef.label.toLowerCase()
            : unitDef.label.toLowerCase();
        return `Wacht ${duration} ${label}`;
      });

      const moveStageSummary = computed(() => {
        const cfg = nodeData.value.config || {};
        const pipelineId = cfg.pipelineId || null;
        const toStageId = cfg.toStageId || null;
        if (!toStageId) return '';

        const pipeline = pipelineId
          ? dealStore.pipelines.find((p) => p.id === pipelineId)
          : null;

        const stages = pipeline?.stages || stageCache.value[pipelineId] || [];
        const stage = stages.find((s) => s.id === toStageId);
        const stageName = stage?.name || String(toStageId);
        return pipeline ? `→ ${stageName} in ${pipeline.name}` : `→ ${stageName}`;
      });

      const memberLabel = (userId) => {
        const member = members.value.find((m) => {
          const user = m.user || m;
          return (user?.id ?? m.user_id) === userId;
        });
        if (!member) return null;
        const user = member.user || member;
        return (
          [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
          user?.email ||
          member.email ||
          null
        );
      };

      const assignSummary = computed(() => {
        const cfg = nodeData.value.config || {};
        const userIds = Array.isArray(cfg.userIds) ? cfg.userIds : [];
        if (!userIds.length) return '';

        const names = userIds.map((id) => memberLabel(id) || 'Onbekend');

        if (cfg.assignmentMode === 'random') {
          if (names.length === 1) return `Willekeurig → ${names[0]}`;
          return `Willekeurig verdelen over ${names.length} personen`;
        }
        return `Toewijzen aan ${names[0]}`;
      });

      const dealTaskSummary = computed(() => {
        const cfg = nodeData.value.config || {};
        const title = (cfg.title || '').trim();
        if (!title) return '';
        const days = parseInt(cfg.dueInDays, 10);
        if (Number.isInteger(days) && days > 0) {
          return `${title} (over ${days} ${days === 1 ? 'dag' : 'dagen'})`;
        }
        return `${title} (vandaag)`;
      });

      const compactSummary = computed(() => {
        switch (nodeData.value.type) {
          case 'integration':
            return integrationName.value || 'Geen integratie gekozen';
          case 'task':
            return taskTypeSummary.value || 'Niet ingesteld';
          case 'move_deal_stage':
            return moveStageSummary.value || 'Geen stage gekozen';
          case 'assign_user':
            return assignSummary.value || 'Geen personen gekozen';
          case 'create_deal_task':
            return dealTaskSummary.value || 'Geen taak ingesteld';
          case 'delay':
            return delaySummary.value || 'Niet ingesteld';
          default:
            return '';
        }
      });

      const runStepMap = inject(
        'automationRunStepMap',
        computed(() => ({}))
      );
      const runClass = computed(() => {
        const status = runStepMap.value?.[props.id]?.status;
        return status ? `action-node--run-${status}` : '';
      });

      onMounted(async () => {
        if (!integrationStore.integrations.length) {
          await integrationStore.fetchIntegrations();
        }
        if (
          nodeData.value.type === 'move_deal_stage' &&
          !dealStore.pipelines.length
        ) {
          await dealStore.fetchPipelines();
        }
        if (nodeData.value.type === 'assign_user') {
          const organizationId = userStore.currentOrganization?.id;
          if (organizationId) {
            try {
              members.value = await organizationService.getMembers(organizationId);
            } catch (error) {
              console.error('Failed to fetch members:', error);
              members.value = [];
            }
          }
        }
      });

      watch(
        nodeData,
        (newVal) => {
          // emit('update', { id: props.id, data: newVal });
        },
        { deep: true }
      );

      const handleActionTypeChange = () => {
        nodeData.value.config = {};
      };

      const addNewNode = () => {
        const modalId = uiStore.addModal(ActionPickerModal, {
          title: 'Add Step',
          description: 'Add a new step to the automation',
          width: 'lg',
          triggerType: props.currentAutomation?.trigger_type || null,
          onSubmit: (action) => {
            emit('add-node', { afterId: props.id, action });
            uiStore.closeModal(modalId);
          },
        });
      };

      return {
        nodeData,
        actionTypes,
        integrationName,
        taskTypeSummary,
        moveStageSummary,
        assignSummary,
        dealTaskSummary,
        delaySummary,
        compactSummary,
        runClass,
        handleActionTypeChange,
        addNewNode,
        hasConnections,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/mixins';

  .action-node {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    width: 240px;
    padding: 0.875rem 1rem;
    background: #fff;
    border: 1px solid var(--slate-200);
    border-radius: 16px;
    color: var(--slate-900);
    outline: 0 solid transparent;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &--active {
      border-color: var(--slate-900);
      box-shadow: 0 0 0 4px var(--slate-100);
    }

    &--run-completed {
      border-color: var(--green-400);
    }

    &--run-failed {
      border-color: var(--red-400);
    }

    &--run-in_progress,
    &--run-pending,
    &--run-scheduled {
      border-color: var(--amber-400);
    }

    &__icon {
      width: 32px;
      height: 32px;
      border-radius: 10px;
      background: var(--slate-100);
      color: var(--slate-700);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    &__body {
      min-width: 0;
      flex: 1;

      h3 {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 600;
        line-height: 1.3;
      }
    }

    &__kind {
      display: block;
      font-size: 0.6875rem;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: var(--slate-500);
      margin-bottom: 0.125rem;
    }

    &__summary {
      margin: 0.25rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(2);
    }

    &__footer {
      display: flex;
      justify-content: center;
      position: absolute;
      bottom: -12px;
      left: 0;
      right: 0;

      .add-node-btn {
        width: 24px;
        height: 24px;
        background: #fff;
        border-radius: 50%;
        border: 1px solid var(--slate-200);
        display: flex;
        align-items: center;
        justify-content: center;

        @include hover {
          background: var(--slate-50);
          border-color: var(--slate-900);
        }
      }
    }
  }
</style>
